/**
 * Product Projections Component
 * 
 * Displays historical and projected data for product financials:
 * - Revenue projections
 * - COGS projections
 * - Margin analysis
 * - Assumption management
 */

import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { Card, Statistic, Tag, Tooltip } from 'antd';
import {
  LineChartOutlined,
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps, PeriodData } from './BaseReportingComponent';
import { fetchProductProjectionsFromLocalBridge } from '../../../services/bridgeSessionDataAdapter';
import { isDriveFlowReportingEnabled } from '../../../services/driveFlowAdapter';
import styles from './ProductProjectionsComponent.module.css';

interface ProjectionPeriod {
  periodId: string;
  periodName: string;
  isProjected: boolean;
  isCurrent: boolean;
  revenue: number;
  revenueLow?: number;
  revenueHigh?: number;
  cogs: number;
  cogsLow?: number;
  cogsHigh?: number;
  margin: number;
}

interface AssumptionSet {
  name: string;
  value: string | number;
  type: 'growth' | 'resource' | 'constraint';
}

interface ProductProjectionsComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Show compact view */
  compact?: boolean;
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

/**
 * Transform period data to projection format
 */
const transformToProjectionData = (
  historicalData: PeriodData[],
  projectionData: PeriodData[],
  currentData: Record<string, any>
): ProjectionPeriod[] => {
  const periods: ProjectionPeriod[] = [];

  // Historical
  historicalData.forEach(period => {
    const revenue = period.values.revenue ?? 0;
    const cogs = period.values.cogs ?? 0;
    periods.push({
      periodId: period.item_id,
      periodName: period.item_name,
      isProjected: false,
      isCurrent: false,
      revenue,
      cogs,
      margin: revenue > 0 ? ((revenue - cogs) / revenue) * 100 : 0
    });
  });

  // Current
  const currentRevenue = currentData.revenue ?? 0;
  const currentCogs = currentData.cogs ?? 0;
  periods.push({
    periodId: 'current',
    periodName: 'Current',
    isProjected: false,
    isCurrent: true,
    revenue: currentRevenue,
    cogs: currentCogs,
    margin: currentRevenue > 0 ? ((currentRevenue - currentCogs) / currentRevenue) * 100 : 0
  });

  // Projections with ranges
  projectionData.forEach(period => {
    const revenueLow = period.values.revenue_low ?? period.values.revenue ?? 0;
    const revenueHigh = period.values.revenue_high ?? period.values.revenue ?? 0;
    const cogsLow = period.values.cogs_low ?? period.values.cogs ?? 0;
    const cogsHigh = period.values.cogs_high ?? period.values.cogs ?? 0;
    const avgRevenue = (revenueLow + revenueHigh) / 2;
    const avgCogs = (cogsLow + cogsHigh) / 2;
    
    periods.push({
      periodId: period.item_id,
      periodName: period.item_name,
      isProjected: true,
      isCurrent: false,
      revenue: avgRevenue,
      revenueLow,
      revenueHigh,
      cogs: avgCogs,
      cogsLow,
      cogsHigh,
      margin: avgRevenue > 0 ? ((avgRevenue - avgCogs) / avgRevenue) * 100 : 0
    });
  });

  return periods;
};

/**
 * D3 Projections Chart
 */
const ProjectionsChart: React.FC<{
  data: ProjectionPeriod[];
  height?: number;
}> = ({ data, height = 280 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const maxValue = useMemo(() => {
    let max = 0;
    data.forEach(d => {
      max = Math.max(max, d.revenueHigh ?? d.revenue, d.cogsHigh ?? d.cogs);
    });
    return max * 1.1;
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || data.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(svgRef.current);
    
    // ISSUE-058/066 fix: Use requestAnimationFrame to batch DOM reads and avoid forced reflows
    const animationFrameId = requestAnimationFrame(() => {
      if (!svgRef.current || !containerRef.current) return;
      
      svg.selectAll('*').remove();

      const margin = { top: 20, right: 30, bottom: 50, left: 70 };
      const width = container.clientWidth;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;

      const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Scales
    const x0 = d3.scaleBand()
      .domain(data.map(d => d.periodId))
      .range([0, innerWidth])
      .padding(0.2);

    const x1 = d3.scaleBand()
      .domain(['revenue', 'cogs'])
      .range([0, x0.bandwidth()])
      .padding(0.1);

    const y = d3.scaleLinear()
      .domain([0, maxValue])
      .nice()
      .range([innerHeight, 0]);

    // X axis
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x0).tickFormat(d => {
        const period = data.find(p => p.periodId === d);
        return period?.periodName ?? d;
      }));

    xAxis.selectAll('text')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em')
      .style('font-size', '11px');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => formatCurrency(d as number)));

    // Draw bars
    data.forEach(period => {
      const periodG = g.append('g')
        .attr('transform', `translate(${x0(period.periodId)},0)`);

      // Current highlight
      if (period.isCurrent) {
        periodG.append('rect')
          .attr('x', -x0.padding() * x0.step() / 2)
          .attr('y', 0)
          .attr('width', x0.bandwidth() + x0.padding() * x0.step())
          .attr('height', innerHeight)
          .attr('fill', '#fef3c7')
          .attr('rx', 4);
        
        periodG.append('text')
          .attr('x', x0.bandwidth() / 2)
          .attr('y', -5)
          .attr('text-anchor', 'middle')
          .attr('fill', '#92400e')
          .style('font-size', '10px')
          .style('font-weight', '600')
          .text('CURRENT');
      }

      if (period.isProjected && period.revenueLow !== undefined) {
        // Revenue range bar
        periodG.append('rect')
          .attr('x', x1('revenue')!)
          .attr('y', y(period.revenueHigh!))
          .attr('width', x1.bandwidth())
          .attr('height', y(period.revenueLow!) - y(period.revenueHigh!))
          .attr('fill', '#22c55e')
          .attr('opacity', 0.4)
          .attr('rx', 2);

        // COGS range bar
        periodG.append('rect')
          .attr('x', x1('cogs')!)
          .attr('y', y(period.cogsHigh!))
          .attr('width', x1.bandwidth())
          .attr('height', y(period.cogsLow!) - y(period.cogsHigh!))
          .attr('fill', '#ef4444')
          .attr('opacity', 0.4)
          .attr('rx', 2);
      } else {
        // Solid bars for historical/current
        periodG.append('rect')
          .attr('x', x1('revenue')!)
          .attr('y', y(period.revenue))
          .attr('width', x1.bandwidth())
          .attr('height', innerHeight - y(period.revenue))
          .attr('fill', '#22c55e')
          .attr('rx', 2);

        periodG.append('rect')
          .attr('x', x1('cogs')!)
          .attr('y', y(period.cogs))
          .attr('width', x1.bandwidth())
          .attr('height', innerHeight - y(period.cogs))
          .attr('fill', '#ef4444')
          .attr('rx', 2);
      }
    });
    }); // End of requestAnimationFrame
    
    // ISSUE-058/066 fix: Cleanup animation frame on unmount
    return () => cancelAnimationFrame(animationFrameId);
  }, [data, height, maxValue]);

  return (
    <div className={styles.chartContainer} ref={containerRef}>
      <svg ref={svgRef} className={styles.chart} />
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.legendColor} style={{ backgroundColor: '#22c55e' }} />
          <span>Revenue</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendColor} style={{ backgroundColor: '#ef4444' }} />
          <span>COGS</span>
        </div>
      </div>
    </div>
  );
};

export const ProductProjectionsComponent: React.FC<ProductProjectionsComponentProps> = ({
  compact = false,
  ...props
}) => {
  const renderContent = (data: ComponentData, loading: boolean) => {
    const projectionData = transformToProjectionData(
      data.historical_data,
      data.projection_data,
      data.data
    );

    const summary = data.data.summary ?? {
      total_projected_revenue: 0,
      total_projected_cogs: 0,
      average_margin: 0,
      growth_rate: 0
    };

    const assumptions: AssumptionSet[] = data.data.assumptions ?? [];

    return (
      <div className={`${styles.productProjections} ${compact ? styles.compact : ''}`}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            <LineChartOutlined /> Product Projections
          </h3>
          <span className={styles.artifactName}>{data.artifact_name}</span>
        </div>

        {/* Summary Stats */}
        <div className={styles.summaryRow}>
          <Card className={styles.summaryCard}>
            <Statistic
              title="Projected Revenue"
              value={summary.total_projected_revenue}
              prefix={<DollarOutlined />}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: '#22c55e' }}
            />
          </Card>
          <Card className={styles.summaryCard}>
            <Statistic
              title="Projected COGS"
              value={summary.total_projected_cogs}
              prefix={<DollarOutlined />}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: '#ef4444' }}
            />
          </Card>
          <Card className={styles.summaryCard}>
            <Statistic
              title="Avg Margin"
              value={summary.average_margin}
              suffix="%"
              valueStyle={{ color: '#818cf8' }}
            />
          </Card>
          <Card className={styles.summaryCard}>
            <Statistic
              title="Growth Rate"
              value={summary.growth_rate}
              suffix="%"
              prefix={summary.growth_rate >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: summary.growth_rate >= 0 ? '#22c55e' : '#ef4444' }}
            />
          </Card>
        </div>

        {/* Projections Chart */}
        <div className={styles.chartSection}>
          <h4 className={styles.sectionTitle}>Revenue vs COGS Over Time</h4>
          <ProjectionsChart data={projectionData} height={compact ? 220 : 280} />
        </div>

        {/* Assumptions */}
        {!compact && assumptions.length > 0 && (
          <div className={styles.assumptionsSection}>
            <h4 className={styles.sectionTitle}>
              <SettingOutlined /> Projection Assumptions
            </h4>
            <div className={styles.assumptionsList}>
              {assumptions.map((assumption, idx) => (
                <div key={idx} className={styles.assumptionItem}>
                  <Tag color={
                    assumption.type === 'growth' ? 'green' :
                    assumption.type === 'resource' ? 'blue' : 'orange'
                  }>
                    {assumption.type}
                  </Tag>
                  <span className={styles.assumptionName}>{assumption.name}</span>
                  <span className={styles.assumptionValue}>{assumption.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const customDataFetcher = isDriveFlowReportingEnabled()
    ? (id: string) => fetchProductProjectionsFromLocalBridge(id)
    : undefined;

  return (
    <BaseReportingComponent
      {...props}
      componentType="product_projections"
      includeHistorical={true}
      customDataFetcher={customDataFetcher}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ProductProjectionsComponent;
